/**
 * NotificationBell — UX-08
 *
 * Bell icon in the navbar (auth users only) with a red badge for unread
 * notifications. Clicking opens a Popover showing the latest 10 items
 * with "Mark all read" CTA. Polls /unread-count every 60 s when the tab is
 * visible (cheap, single integer payload).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, Inbox, ExternalLink } from 'lucide-react';
import {
  Popover, PopoverTrigger, PopoverContent,
} from './ui/popover';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u?.token ? { Authorization: `Bearer ${u.token}` } : {};
  } catch { return {}; }
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'à l\u2019instant';
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const ICONS_BY_TYPE = {
  new_inquiry:        '📩',
  new_inquiry_reply:  '💬',
  expiry_soon:        '⏰',
  review_received:    '⭐',
  payment_completed:  '💳',
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications/me/unread-count`, { headers: authHeader() });
      setUnread(Number(r.data?.unread_count || 0));
    } catch { /* silent */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications/me?limit=10`, { headers: authHeader() });
      setItems(r.data?.items || []);
      setUnread(Number(r.data?.unread_count || 0));
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchUnread();
    // Poll every 60 s when document is visible
    const tick = () => {
      if (document.visibilityState === 'visible') fetchUnread();
    };
    const id = setInterval(tick, 60_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [fetchUnread]);

  // When opening, refresh the list
  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/me/read-all`, {}, { headers: authHeader() });
      setItems((arr) => arr.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const handleItemClick = async (n) => {
    // Mark this one read then navigate
    try {
      if (!n.read_at) {
        await axios.post(`${API}/notifications/me/${n.id}/read`, {}, { headers: authHeader() });
        setUnread((u) => Math.max(0, u - 1));
      }
    } catch { /* silent */ }
    setOpen(false);
    if (n.link) {
      // React Router for internal links
      if (n.link.startsWith('/')) navigate(n.link);
      else window.location.href = n.link;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={unread > 0 ? `${unread} notifications non lues` : 'Notifications'}
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-stone-100 transition-colors text-stone-700 focus:outline-none focus:ring-2 focus:ring-orange-300"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-label font-bold flex items-center justify-center ring-2 ring-white"
              data-testid="notification-bell-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0 max-h-[480px] overflow-hidden flex flex-col"
        data-testid="notification-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <p className="text-sm font-bold text-stone-900 flex items-center gap-2">
            <Inbox className="h-4 w-4 text-orange-600" />
            Notifications
            {unread > 0 && (
              <span className="text-xs font-medium text-red-600">({unread} non lue{unread > 1 ? 's' : ''})</span>
            )}
          </p>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllRead}
              className="text-xs text-stone-600 hover:text-stone-900 h-7 px-2"
              data-testid="notification-mark-all-read"
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Tout marquer lu
            </Button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && items.length === 0 && (
            <div className="p-4 space-y-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-12 bg-stone-100 rounded animate-pulse" />
              ))}
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="p-6 text-center" data-testid="notification-empty">
              <Bell className="h-10 w-10 text-stone-300 mx-auto mb-2" />
              <p className="text-sm text-stone-600 font-medium">Aucune notification</p>
              <p className="text-xs text-stone-400 mt-1">
                Les nouveaux messages et événements importants apparaîtront ici.
              </p>
            </div>
          )}

          {items.length > 0 && (
            <ul className="divide-y divide-stone-100">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(n)}
                    className={`w-full text-left px-4 py-3 hover:bg-stone-50 transition-colors flex gap-3 items-start ${
                      !n.read_at ? 'bg-orange-50/40' : ''
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <span className="text-lg shrink-0" aria-hidden>
                      {ICONS_BY_TYPE[n.type] || '🔔'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={`text-sm leading-snug ${!n.read_at ? 'font-semibold text-stone-900' : 'text-stone-700'}`}
                        >
                          {n.title}
                        </p>
                        {!n.read_at && <span className="shrink-0 mt-1 h-2 w-2 rounded-full bg-orange-500" />}
                      </div>
                      {n.body && (
                        <p className="text-xs text-stone-600 line-clamp-2 mt-0.5">{n.body}</p>
                      )}
                      <p className="text-label text-stone-400 mt-1">{formatRelative(n.created_at)}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-stone-100 px-4 py-2 flex justify-between items-center text-xs">
          <Link
            to="/dashboard?tab=messages"
            onClick={() => setOpen(false)}
            className="text-orange-700 hover:text-orange-900 font-semibold inline-flex items-center gap-1"
            data-testid="notification-go-messages"
          >
            Voir tous les messages
            <ExternalLink className="h-3 w-3" />
          </Link>
          <Link
            to="/dashboard?tab=profil"
            onClick={() => setOpen(false)}
            className="text-stone-500 hover:text-stone-700"
            data-testid="notification-go-settings"
          >
            Paramètres
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
